import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  NativeResources,
  Resources,
  SearchTermSubCategory,
} from '@ariksa/inventory-core';
import { NotificationFor } from '@ariksa/notification/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { find, isEmpty, map, slice, some, split } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import { customTheme } from 'theme';
import { Dict } from 'types/utils';

import { useFlags } from 'app/flagsmith';
import {
  AriksaIcon,
  formatBytes,
  formatTooltip,
  getIcon,
  getIconColor,
  renderCloudIcon,
  renderRiskContext,
  renderSeverityBar,
  renderStackedCloudNameAndIcon,
  renderTableHeaderWithLoader,
  renderTimeStackedCell,
  renderTooltipFieldValue,
  StackedCell,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { ActionButton } from 'components/DataEntry';
import {
  GraphIcon,
  IconTypes,
  LinkIcon,
  ListIcon,
  PackagesIcon,
  SearchIcon,
} from 'components/Icons';
import { IMenuItemProps, TableActionsMenu } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { renderCVSSScore } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/utils';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { selectApp } from 'containers/App/selectors';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { NotificationResource } from 'containers/Findings/Alerts/types';
import { renderVulnerabilities } from 'containers/Inventory/CloudInventory/Components/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { VulnerabilityScanConfirmation } from 'containers/SharedState/Components/VulnerabilityScanConfirmation';
import { useRedirectToSecurityGraph } from 'containers/Visibility/SecurityGraphNext/hooks/useRedirectToSecurityGraph';
import {
  getScanningStatus,
  renderCloudEntities,
  renderImageName,
  renderOSNameAndIcon,
  renderPackagesImpacted,
  renderPackageType,
} from 'containers/Visibility/Vulnerabilities/Components/hooks/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import {
  CONTAINER_ON_VIRTUAL_MACHINE,
  VulnerabilityViewByTabs,
} from 'containers/Visibility/Vulnerabilities/types';
import { useSearchParams } from 'hooks/useSearchParams';

export const useVulnerabilityTableColumns = () => {
  const { new_vuln_dashboard: vulnerabilityV3Flag } = useFlags([
    'new_vuln_dashboard',
  ]);
  const {
    resourceType,
    vulnerabilityType,
    cveHostsRiskContext,
    cveHostsVulnerabilities,
    publishedAt,
    riskContext,
  } = useSelector(selectVulnerabilities);
  const { accountMapping, renderStackedAccountNameID } = useCloudAccountId();
  const { getCloudNativeName } = useResourceType();
  const ticketModal = useDisclosure();
  const statusDisclosure = useDisclosure();
  const notificationModal = useDisclosure();
  const hostsDisclosure = useDisclosure();
  const pageLocation = useLocation();
  const { user } = useSelector(selectApp);
  const dispatch = useDispatch();

  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const { getTabIndex } = useMetadataDrawerTabs();
  const { redirectToSecurityGraph } = useRedirectToSecurityGraph();
  const params = useSearchParams<{ tab: string }>();
  const { environmentId } = useAccessBoundary();

  const [currentRow, setCurrentRow] = useState<Dict<any> | null>(null);
  const [currentCVE, setCurrentCVE] = useState<Dict<any>>({});
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsCopied(false);
    }, 9000);

    return () => clearInterval(interval);
  }, []);

  const handleRowClick = useCallback(
    (r, tabType) => {
      let resource =
        resourceType === NativeResources.ElasticContainerRegistry
          ? NativeResources.ElasticContainerRegistry
          : r?.native_name;
      const tabIndex = getTabIndex(resource, tabType);

      updateActiveResource({
        resourceType: resource as NativeResources,
        uuid: r?.registry_uuid ?? r?.uuid,
        accountId: r?.account_uuid ?? r?.account,
        resourceId: r?.registry_id ?? r?.resource_id,
        showResolvedVulnerabilities: vulnerabilityType === 'resolved',
        vulnerabilityPublishedTime: publishedAt,
        cloudProvider: r?.provider,
        searchTerm:
          resourceType === NativeResources.ElasticContainerRegistry
            ? r?.image_tags?.[0]
            : undefined,
      });
      onOpenMetadata(tabIndex);
    },
    [
      onOpenMetadata,
      updateActiveResource,
      getTabIndex,
      vulnerabilityType,
      publishedAt,
      resourceType,
    ],
  );

  const getTab = useCallback(() => {
    switch (resourceType) {
      case Resources.VirtualMachine:
      case NativeResources.ElasticContainerService:
      case CONTAINER_ON_VIRTUAL_MACHINE:
        return MetadataTabs.Network;
      default:
        return MetadataTabs.Summary;
    }
  }, [resourceType]);

  const renderResourceActions = useCallback(
    ({ row }) => {
      const items: IMenuItemProps[] = [
        {
          label: 'Show security graph',
          onClick: () => {
            const { native_name, uuid, resource_id, account } = row;
            redirectToSecurityGraph({
              mapType: 'posture',
              resourceType:
                resourceType === NativeResources.ElasticContainerRegistry
                  ? 'ContainerImage'
                  : native_name,
              resourceUuid:
                resourceType === NativeResources.ElasticContainerRegistry
                  ? row?.id
                  : uuid,
              resourceId: resource_id,
              account: account ?? row?.account_uuid,
              sourceTab: MetadataTabs.Vulnerabilities,
              ...(resourceType === NativeResources.ElasticContainerRegistry
                ? {}
                : { subCategory: SearchTermSubCategory.Vulnerable }),
            });
            setCurrentRow(row);
            handleRowClick(row, getTab());
          },
          icon: <GraphIcon />,
        },
        {
          label:
            isCopied && row?.uuid === currentRow?.uuid
              ? 'Copied to clipboard'
              : 'Copy link for vulnerabilities',
          onClick: () => {
            setIsCopied(true);
            setCurrentRow(row);
            navigator.clipboard.writeText(
              (
                pageLocation.origin +
                '/visibility/vulnerability/resource/' +
                (row?.registry_uuid ?? row?.uuid) +
                '?environmentId=' +
                environmentId +
                (resourceType === NativeResources.ElasticContainerRegistry
                  ? '&imageName=' + row?.image_tags?.[0]
                  : '') +
                '&organization=' +
                user.info.org_id
              ).toString(),
            );
          },
          icon: <LinkIcon />,
        },
        {
          label: 'Scan now',
          onClick: () => {
            setCurrentRow(row);
            statusDisclosure.onOpen();
          },
          icon: <SearchIcon />,
        },
        /*{
          label: 'Create ticket',
          onClick: () => {
            setCurrentRow(row);
            ticketModal.onOpen();
          },
          icon: <TicketIcon />,
          isDisabled: true,
        },
        {
          label: 'Notify Owner',
          onClick: () => {
            setCurrentRow(row);
            notificationModal.onOpen();
          },
          icon: <NotificationIcon />,
        },*/
      ];

      return (
        <Center onClick={e => e.stopPropagation()}>
          <TableActionsMenu menuItems={items} />
        </Center>
      );
    },
    [
      getTab,
      handleRowClick,
      notificationModal,
      redirectToSecurityGraph,
      //params.tab,
      resourceType,
      user.info.org_id,
      pageLocation.origin,
      isCopied,
      currentRow,
      ticketModal,
      environmentId,
      dispatch,
    ],
  );

  const renderActions = useCallback(
    ({ row }) => {
      return (
        <Center onClick={e => e.stopPropagation()}>
          {params.tab !== 'cve' && (
            <Center>
              <ActionButton
                label="Show security graph"
                icon={<GraphIcon />}
                onClick={() => {
                  const { native_name, uuid, resource_id, account } = row;
                  redirectToSecurityGraph({
                    mapType: 'posture',
                    resourceType:
                      resourceType === NativeResources.ElasticContainerRegistry
                        ? 'ContainerImage'
                        : native_name,
                    resourceUuid:
                      resourceType === NativeResources.ElasticContainerRegistry
                        ? row?.id
                        : uuid,
                    resourceId: resource_id,
                    account: account ?? row?.account_uuid,
                    sourceTab: MetadataTabs.Vulnerabilities,
                    ...(resourceType ===
                    NativeResources.ElasticContainerRegistry
                      ? {}
                      : { subCategory: SearchTermSubCategory.Vulnerable }),
                  });
                  setCurrentRow(row);
                  handleRowClick(row, getTab());
                }}
              />
            </Center>
          )}
          {/*<ActionButton
            label="Create ticket"
            icon={<TicketIcon />}
            onClick={() => {
              setCurrentRow(row);
              ticketModal.onOpen();
            }}
          />*/}
          {/*<Center>
            <ActionButton
              label="Send notification"
              icon={<NotificationIcon />}
              onClick={() => {
                setCurrentRow(row);
                notificationModal.onOpen();
              }}
            />
          </Center>*/}
          <Center>
            <ActionButton
              label={
                isCopied && row?.uuid === currentRow?.uuid
                  ? 'Copied to clipboard'
                  : 'Copy link for resource vulnerabilities'
              }
              tooltipWidth={32}
              icon={<LinkIcon />}
              onClick={() => {
                setIsCopied(true);
                setCurrentRow(row);
                navigator.clipboard.writeText(
                  (
                    pageLocation.origin +
                    '/visibility/vulnerability/resource/' +
                    (row?.registry_uuid ?? row?.uuid) +
                    '?environmentId=' +
                    environmentId +
                    (resourceType === NativeResources.ElasticContainerRegistry
                      ? '&imageName=' + row?.image_tags?.[0]
                      : '') +
                    '&organization=' +
                    user.info.org_id
                  ).toString(),
                );
              }}
            />
          </Center>
        </Center>
      );
    },
    [
      getTab,
      handleRowClick,
      notificationModal,
      redirectToSecurityGraph,
      params.tab,
      resourceType,
      user.info.org_id,
      pageLocation.origin,
      isCopied,
      currentRow,
      //ticketModal,
      environmentId,
    ],
  );

  let columns: TableColumnProps[];

  const host = {
    header: (
      <Box pl="thLeftPaddingWithIcon">
        {resourceType === NativeResources.LambdaFunction ? 'Function' : 'Host'}
      </Box>
    ),
    accessor: '',
    render: ({ row }) => {
      let resource = resourceType;
      if (
        resourceType === NativeResources.ElasticContainerService ||
        resourceType === CONTAINER_ON_VIRTUAL_MACHINE
      )
        resource = NativeResources.Container;
      return (
        <HStack w="full" justify="space-between">
          <Box flex={1} minW={0}>
            <Box w="full">
              <WithResourceIcon
                resourceType={resource as string}
                iconSize="9"
                iconTooltip={
                  <ResourceTypeIconTooltip resourceType={row?.native_name} />
                }
              >
                <StackedCell
                  upper={row?.name ?? row?.container_id}
                  lower={row?.resource_id ?? 'Container'}
                  tooltip={formatTooltip({
                    header: 'RESOURCE',
                    content: (
                      <Stack>
                        {renderTooltipFieldValue(
                          'Name',
                          row?.name || row?.container_id,
                        )}
                        {renderTooltipFieldValue('ID', row?.resource_id)}
                        {renderTooltipFieldValue(
                          'Type',
                          getCloudNativeName(row?.native_name),
                        )}
                      </Stack>
                    ),
                    width: 'full',
                  })}
                />
              </WithResourceIcon>
            </Box>
          </Box>
          {(row?.status === 'running' || row?.status === 'waiting') && (
            <Box boxSize={5} flexShrink={0}>
              <CustomTooltip label={getScanningStatus(row)}>
                <Center>
                  {getIcon(
                    row?.status === 'running'
                      ? IconTypes.Search
                      : IconTypes.ScanningQueue,
                  )}
                </Center>
              </CustomTooltip>
            </Box>
          )}
        </HStack>
      );
    },
    align: 'left',
  };

  const image = {
    header: <Box pl="thLeftPaddingWithIcon">Image</Box>,
    accessor: 'image',
    render: ({ row }) => renderImageName(row, resourceType),
    align: 'left',
  };

  const cloud = {
    header: 'Cloud',
    align: 'left',
    render: ({ row }) => {
      const provider =
        accountMapping[row?.account]?.cloud_type ?? row?.cloud ?? '';
      return !!provider ? renderCloudIcon({ provider }) : '';
    },
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const account = {
    header: 'Account',
    align: 'left',
    render: ({ row }) => {
      return renderStackedAccountNameID(row?.account ?? row?.account_uuid);
    },
    styles: { width: '200px', cell: { maxWidth: '200px' } },
  };

  const containerCloud = {
    header: 'Cloud',
    align: 'left',
    render: ({ row }) => {
      const account = accountMapping[row?.account];
      return (
        <Stack spacing={1}>
          <Box>
            {renderStackedCloudNameAndIcon(account?.cloud_type ?? 'aws', true)}
          </Box>
          <Box>{account?.name ?? account?.cloud_account_id}</Box>
        </Stack>
      );
    },
  };

  const deployedInVpc = {
    header: 'Deployed In',
    align: 'left',
    styles: { width: '180px', cell: { maxWidth: '180px' } },
    render: ({ row }) => {
      return row?.vpc || row?.vpc_id ? (
        <CustomTooltip
          label={
            <Stack>
              {renderTooltipFieldValue('VPC Name', row?.vpc || '-')}
              {renderTooltipFieldValue('VPC ID', row?.vpc_id || '-')}
            </Stack>
          }
        >
          {row?.vpc || row?.vpc_id}
        </CustomTooltip>
      ) : (
        '-'
      );
    },
  };

  const deployedInContainer = {
    header: 'Deployed In',
    accessor: 'deployed_in',
    align: 'left',
    //styles: { width: '150px', cell: { maxWidth: '150px' } },
    render: ({ value, row }) => {
      const renderValue = (val, type) => {
        return (
          <CustomTooltip
            label={
              <Stack>
                <Box color="orange">{getCloudNativeName(type)}</Box>
                <Box>{val}</Box>
              </Stack>
            }
          >
            <HStack w="full" spacing={1} align="center">
              <Box boxSize={5} color={customTheme.colors.grey['250']}>
                <Center>{getIcon(type, 'inherit')}</Center>
              </Box>
              <Box w="full" textOverflow="ellipsis" overflow="hidden">
                {val}
              </Box>
            </HStack>
          </CustomTooltip>
        );
      };

      const gce = value?.gce || value?.gce_id;
      const gke = value?.gcpgke || value?.gcpgke_id;
      const ec2 = value?.ec2 || value?.ec2_id;
      const ecs = value?.ecs || value?.ecs_id;
      const eks = value?.eks || value?.eks_id;
      const avm = value?.avm || value?.avm_id;
      const avmssInstance = value?.avmss_instance || value?.avmss_instance_id;
      const aks = value?.aks || value?.aks_id;

      return (
        <Stack spacing={0}>
          {(value?.gce || value?.gke) && (
            <StackedCell
              upper={
                gce
                  ? renderValue(gce, NativeResources.GcpComputeEngine)
                  : gke
                  ? renderValue(gke, NativeResources.Gcpgke)
                  : '-'
              }
              lower={gce && gke ? renderValue(gke, NativeResources.Gcpgke) : ''}
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          )}

          {(value?.ec2 || value?.ecs || value?.eks) && (
            <StackedCell
              upper={
                ec2
                  ? renderValue(ec2, NativeResources.Ec2Instance)
                  : ecs
                  ? renderValue(ecs, NativeResources.ElasticContainerService)
                  : '-'
              }
              lower={
                ec2 && ecs
                  ? renderValue(ecs, NativeResources.ElasticContainerService)
                  : eks
                  ? renderValue(eks, NativeResources.ElasticKubernetesService)
                  : ''
              }
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          )}

          {(avm || avmssInstance || aks) && (
            <StackedCell
              upper={
                avm
                  ? renderValue(avm, NativeResources.AzureVirtualMachine)
                  : avmssInstance
                  ? renderValue(
                      avmssInstance,
                      NativeResources.AzureVirtualMachineScaleSets,
                    )
                  : '-'
              }
              lower={
                avm && avmssInstance
                  ? renderValue(
                      avmssInstance,
                      NativeResources.AzureVirtualMachineScaleSets,
                    )
                  : aks
                  ? renderValue(aks, NativeResources.Aks)
                  : ''
              }
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          )}
        </Stack>
      );
    },
  };

  const deployedFromRegistry = {
    header: 'Registry',
    align: 'left',
    //styles: { width: '150px', cell: { maxWidth: '150px' } },
    render: ({ row }) => {
      return row?.repository_name ?? row?.uri;
      /*<StackedCell
        lower={row?.registry_id}
        upper={row?.repository_uri ?? row?.repository_name}
      />;*/
    },
    styles: { width: '130px', cell: { maxWidth: '130px' } },
  };

  const actions = {
    header: 'Action',
    render: renderActions,
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const resourceActions = {
    header: 'Action',
    render: renderResourceActions,
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const createdAt = {
    header: 'Created At',
    align: 'left',
    accessor: 'created_at',
    render: renderTimeStackedCell,
  };

  const context = {
    header: renderTableHeaderWithLoader('Risk Context', riskContext.isLoading),
    align: 'left',
    render: ({ row }) => renderRiskContext(riskContext.data?.[row?.uuid]),
    styles: { width: '100px', cell: { maxWidth: '100px' } },
  };

  const technology = {
    header: 'Technologies',
    accessor: 'technologies',
    align: 'left',
    render: ({ row }) => {
      const data = row?.applications;
      const renderTag = index =>
        !!data?.[index] && (
          <CustomTooltip label={data[index]}>
            <AriksaIcon
              border={true}
              type={data[index]}
              iconFilled={false}
              borderColor={customTheme.colors.gray['200']}
              bg={customTheme.colors.gray['50']}
            />
          </CustomTooltip>
        );
      return data?.length ? (
        <Stack spacing={1}>
          <HStack spacing={1}>
            {renderTag(0)}
            {renderTag(1)}
            {renderTag(2)}
            {renderTag(4)}
          </HStack>
          <HStack spacing={1}>
            {renderTag(5)}
            {renderTag(6)}
            {renderTag(7)}
            {data?.length > 8 && (
              <CustomTooltip
                label={
                  <Stack>
                    {map(slice(data, 8), o => (
                      <Box>{o as string}</Box>
                    ))}
                  </Stack>
                }
              >
                <Tag label={'+ ' + (data?.length - 8)} />
              </CustomTooltip>
            )}
          </HStack>
        </Stack>
      ) : (
        '-'
      );
    },
  };

  const containerOs = {
    header: 'Container OS',
    align: 'left',
    styles: { width: '150px', cell: { maxWidth: '150px' } },
    render: ({ row }) => (
      <StackedCell
        upper={
          row?.os_name ??
          '-' /*+
          ' ' +
          (summary?.[row?.uuid]?.os_version ?? '')*/
        }
        lower={
          <HStack>
            <Box>{row?.os_version ?? ''}</Box>
            {/*Is Latest: -*/}
          </HStack>
        }
      />
    ),
  };

  const os = {
    header: 'OS',
    align: 'left',
    //styles: { width: '100px', cell: { maxWidth: '100px' } },
    render: ({ row }) => (
      <StackedCell upper={row?.os_name} lower={row?.os_version} />
    ),
  };

  const vulnerability = {
    header: 'Vulnerabilities',
    accessor: 'vulnerabilities',
    align: 'left',
    render: renderVulnerabilities,
    //styles: { width: '300px', cell: { maxWidth: '300px' } },
  };

  const sizeDigest = {
    header: 'Size / Digest',
    accessor: 'vulnerabilities',
    align: 'left',
    render: ({ row }) => (
      <CustomTooltip
        label={
          <Stack>
            {renderTooltipFieldValue(
              'Size',
              formatBytes(row?.image_size_in_bytes),
            )}
            {renderTooltipFieldValue('Digest', row?.image_digest)}
          </Stack>
        }
      >
        <StackedCell
          upper={formatBytes(row?.image_size_in_bytes)}
          lower={row?.image_digest}
          showUpperTooltip={false}
          showLowerTooltip={false}
        />
      </CustomTooltip>
    ),
    styles: { width: '200px', cell: { maxWidth: '200px' } },
  };
  const platform = {
    header: 'Architecture',
    accessor: 'architecture',
    align: 'left',
    //styles: { width: '80px', cell: { maxWidth: '80px' } },
    //render: ({ row }) => <StackedCell upper={'-'} lower={row?.architecture} />,
  };
  const device = {
    header: 'Device',
    accessor: 'vulnerabilities',
    align: 'left',
    render: ({ row }) => {
      const deviceMapping = find(
        map(row?.block_device_mappings, o => JSON.parse(o)),
        { DeviceName: row?.root_device_name },
      );
      const encrypted =
        (deviceMapping?.Ebs?.Encrypted ? '' : 'Not ') + 'Encrypted';
      return <StackedCell upper={row?.root_device_name} lower={encrypted} />;
    },
    styles: { width: '150px', cell: { maxWidth: '150px' } },
  };
  const location = {
    header: 'Location',
    accessor: 'vulnerabilities',
    align: 'left',
    render: ({ row }) => (
      <StackedCell
        upper={row?.public ? 'Public' : 'Private'}
        lower={row?.image_location}
      />
    ),
    styles: { width: '150px', cell: { maxWidth: '150px' } },
  };

  const entities = {
    header: 'Entities',
    accessor: 'entities',
    render: renderCloudEntities,
    align: 'left',
  };

  const renderTooltip = (field, value) => (
    <HStack align="flex-start">
      <Box color="orange" whiteSpace="nowrap">
        {field}:
      </Box>
      <Box>{value}</Box>
    </HStack>
  );

  const lastCveSeen = {
    header: vulnerabilityType === 'resolved' ? 'Patched At' : 'Last CVE Seen',
    accessor: 'last_seen',
    render: renderTimeStackedCell,
    align: 'left',
  };

  const lastUpdate = {
    header: vulnerabilityType === 'resolved' ? 'Patched At' : 'Last Update',
    render: ({ row }) =>
      renderTimeStackedCell({ value: row?.last_seen ?? row?.last_scanned_at }),
    align: 'left',
  };

  const virtualMachine = [
    host,
    {
      header: 'Image',
      accessor: 'image',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={IconTypes.MachineImage}
          color={customTheme.colors.blue['300']}
          tooltip={value}
        >
          {value}
        </WithResourceIcon>
      ),
      align: 'left',
    },
    vulnerability,
    context,
    account,
    deployedInVpc,
    //os,
    lastCveSeen,
    ...(vulnerabilityType === 'resolved' ? [] : [resourceActions]),
  ];

  const serverless = [
    host,
    vulnerability,
    context,
    account,
    { header: 'Deployed In', accessor: 'region', align: 'left' },
    { header: 'Runtime', accessor: 'runtime', align: 'left' },
    lastCveSeen,
    ...(vulnerabilityType === 'resolved' ? [] : [resourceActions]),
  ];

  const commonContainerColumns = [
    host,
    {
      header: 'Image',
      accessor: 'image',
      align: 'left',
      //styles: { width: '150px', cell: { maxWidth: '150px' } },
      render: ({ value, row }) => {
        return (
          <WithResourceIcon
            resourceType={IconTypes.Image}
            iconSize="regular"
            color={customTheme.colors.skyBlue['300']}
            border={false}
            tooltip={value}
          >
            {value}
          </WithResourceIcon>
        );
      },
    },
    vulnerability,
    deployedInContainer,
    //containerOs,
    //technology,
    { header: 'Namespace', accessor: 'namespace', align: 'left' },
    lastCveSeen,
  ];

  const containers = [
    ...commonContainerColumns,
    ...(vulnerabilityType === 'resolved' ? [] : [actions]),
  ];

  const vmContainerColumns = [
    ...commonContainerColumns,
    ...(vulnerabilityType === 'resolved' ? [] : [resourceActions]),
  ];

  const containerImages = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Image</Box>,
      accessor: 'image',
      render: ({ row }) => renderImageName(row, resourceType),
      align: 'left',
    },
    vulnerability,
    account,
    {
      header: 'Containers Deployed',
      accessor: 'containers_count',
      //styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    {
      header: 'Source',
      align: 'left',
      render: ({ value, row }) => {
        const registryName = split(row?.repository_uri, '/')?.[0] ?? '';
        return (
          <StackedCell
            upper={registryName}
            lower={row?.repository_name}
            showLowerTooltip={false}
            showUpperTooltip={false}
            tooltip={
              <Stack>
                {renderTooltipFieldValue('Registry', registryName)}
                {renderTooltipFieldValue('Repository', row?.repository_name)}
              </Stack>
            }
          />
        );
      },
      //styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    //os,
    lastUpdate,
    //sizeDigest,
    //createdAt,
    //version,
    ...(vulnerabilityType === 'resolved' ? [] : [actions]),
  ];
  const machineImages = [
    image,
    vulnerability,
    account,
    //platform,
    //device,
    {
      header: 'Hosts Deployed',
      accessor: 'vm_count',
      styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    //createdAt,
    //location,
    {
      header: vulnerabilityType === 'resolved' ? 'Patched At' : 'Last Update',
    },
    ...(vulnerabilityType === 'resolved' ? [] : [resourceActions]),
  ];

  const cveTopCommonColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">CVE</Box>,
      accessor: 'vulnerability_id',
      sortKey: 'vulnerability_id',
      render: ({ row }) => {
        const description =
          row?.title === row?.vulnerability_id ? row?.description : row?.title;
        return (
          <WithResourceIcon
            resourceType={IconTypes.Vulnerabilities}
            color={getIconColor(row?.severity)}
            tooltip={formatTooltip({
              header: row?.vulnerability_id,
              content: row?.description,
              width: 72,
            })}
          >
            <StackedCell
              upper={row?.vulnerability_id}
              lower={description}
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          </WithResourceIcon>
        );
      },
      //styles: { width: '600px', cell: { maxWidth: '600px' } },
    },
    ...(params.tab === VulnerabilityViewByTabs.Package
      ? []
      : [
          vulnerabilityV3Flag && params.tab === VulnerabilityViewByTabs.CVE
            ? {
                header: 'Affected Resources',
                accessor: 'affected_resource_count',
                align: 'left',
                render: ({ row }) =>
                  row?.affected_resource_count ?? row?.entities?.total ?? 0,
                styles: { width: '150px', cell: { maxWidth: '150px' } },
              }
            : {
                header: 'Package',
                accessor: 'package_name',
                align: 'left',
                render: ({ row }) => (
                  <StackedCell
                    upper={row?.package_name}
                    lower={row?.installed_version}
                    showUpperTooltip={false}
                    showLowerTooltip={false}
                  />
                ),
                styles: { width: '200px', cell: { maxWidth: '200px' } },
              },
        ]),
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      sortKey: 'severity',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'CVSS Score',
      render: ({ row }) => {
        const value =
          row?.cvss_v3?.score || row?.cvss_v4?.score || row?.cvss_score;
        const label = !!row?.cvss_v3?.score
          ? 'CVSS V3: ' + value
          : !!row?.cvss_v4?.score
          ? 'CVSS V4: ' + value
          : 'CVSS: ' + value;
        return (
          <CustomTooltip label={label}>{renderCVSSScore(row)}</CustomTooltip>
        );
      },
      accessor: 'cvss_v3_score',
      sortKey: 'cvss_v3_score',
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    ...(params.tab === VulnerabilityViewByTabs.Package
      ? []
      : [
          {
            header: 'Patch Version',
            accessor: 'fixed_in_version',
            align: 'left',
            styles: { width: '200px', cell: { maxWidth: '200px' } },
          },
        ]),
  ];

  const cveBottomCommonColumns = [
    /*{
      header: 'Description',
      accessor: 'description',
      align: 'left',
      styles: { width: '500px', cell: { maxWidth: '600px' } },
      render: ({ row }) => (
        <CustomTooltip label={row?.description}>
          <Center
            w={'full'}
            whiteSpace="pre-line"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight={'1.5em'}
            h={'3em'}
            justifyContent="left"
            alignItems="top"
            position="relative"
          >
            {row?.description}
          </Center>
        </CustomTooltip>
      ),
    },*/
    {
      header: 'Has Public Exploit',
      render: ({ row }) => {
        return row.exploitable ? 'Yes' : 'No';
      },
      styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    ...(params.tab === VulnerabilityViewByTabs.Package
      ? [
          {
            header: 'Updated At',
            accessor: 'last_modified_date',
            render: renderTimeStackedCell,
          },
        ]
      : []),
    /*{
      header: 'Requires Network Access',
      render: ({ row }) => {
        return row?.cvss_v3?.attack_vector === 'Network' ? 'Yes' : 'No';
      },
      styles: { width: '100px', cell: { maxWidth: '100px' } },
    },*/
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            label={'Show all entities'}
            icon={<ListIcon />}
            onClick={e => {
              e.stopPropagation();
              hostsDisclosure.onOpen();
              setCurrentCVE(row);
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  const cveColumns = [...cveTopCommonColumns, ...cveBottomCommonColumns];

  const cveUnderPackagesColumns = [
    ...cveTopCommonColumns,
    /*{
      header: 'Patch Version',
      accessor: 'fixed_in_version',
      align: 'left',
      styles: { width: '250px', cell: { maxWidth: '250px' } },
    },*/
    ...cveBottomCommonColumns,
  ];

  const cveHostsColumns = currentRecord => [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      accessor: 'name',
      align: 'left',
      //sortKey: 'name',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.native_name}
          iconTooltip={
            <ResourceTypeIconTooltip resourceType={row?.native_name} />
          }
          iconSize="9"
          color={!row?.native_name ? customTheme.colors.primary : undefined}
        >
          <CustomTooltip
            label={
              <Stack>
                {renderTooltip('Name', row?.name)}
                {renderTooltip('Resource ID', row?.resource_id)}
                {row?.pod && renderTooltip('POD', row?.pod)}
                {row?.instance_id &&
                  renderTooltip('Instance ID', row?.instance_id)}
              </Stack>
            }
          >
            <StackedCell
              upper={row?.name ?? '-'}
              lower={row?.resource_id}
              showUpperTooltip={false}
              showLowerTooltip={false}
            />
          </CustomTooltip>
        </WithResourceIcon>
      ),
    },
    {
      header: renderTableHeaderWithLoader(
        'Image',
        some(cveHostsVulnerabilities, { isLoading: true }),
      ),
      render: ({ row }) => {
        // OS info for a resource type is absent until its request starts.
        const data =
          cveHostsVulnerabilities?.[row?.native_name]?.data?.[row?.uuid];
        return (
          <StackedCell
            upper={row?.image}
            lower={renderOSNameAndIcon(data)}
            tooltip={
              <Stack>
                {renderTooltip('Image', row?.image || '-')}
                {renderTooltip('OS Name', data?.os_name || 'Unknown')}
                {renderTooltip('OS Version', data?.os_version || '-')}
              </Stack>
            }
          />
        );
      },
      align: 'left',
    },
    {
      header: renderTableHeaderWithLoader(
        'Risk Context',
        cveHostsRiskContext.isLoading,
      ),
      align: 'left',
      render: ({ row }) =>
        renderRiskContext(cveHostsRiskContext.data?.[row?.uuid]),
      //styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    {
      header: 'Patch Version',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={currentRecord?.package_name}
          lower={
            currentRecord?.fixed_in_version ??
            (!isEmpty(currentRecord?.patch_versions)
              ? currentRecord?.patch_versions?.join(', ')
              : '-')
          }
        />
      ),
    },
    {
      header: 'Account',
      align: 'left',
      render: ({ row }) =>
        renderStackedAccountNameID(row?.account ?? row?.account_uuid),
    },
    {
      header: 'Deployed in',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.vpc ?? row?.vpc_id}
          lower={row?.region}
          showLowerTooltip={false}
        />
      ),
    },
    /*{
      header: 'State',
      align: 'left',
      render: ({ row }) =>
        row?.state === 'running' ? (
          <WithResourceIcon
            resourceType={IconTypes.SyncRestart}
            iconFilled={false}
            iconSize="xs"
            color={customTheme.colors.gray['300']}
            spacing={1}
          >
            {toTitleCase(row?.state ?? '')}
          </WithResourceIcon>
        ) : (
          ''
        ),
    },*/
    actions,
  ];
  const packagesColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Package Name</Box>,
      accessor: 'package_name',
      align: 'left',
      //sortKey: 'name',
      render: ({ value, row }) => (
        <WithResourceIcon
          resourceType={row?.package_category_name ?? IconTypes.Packages}
          defaultIcon={<PackagesIcon />}
          tooltip={value}
        >
          <StackedCell upper={value} lower={row?.installed_version} />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Patch Version',
      accessor: 'patch_versions',
      align: 'left',
      render: ({ row, value }) =>
        value ? row?.patch_versions?.join(', ') : '-',
    },
    {
      header: 'Type',
      accessor: 'package_category',
      align: 'left',
      render: ({ value }) => renderPackageType(value),
    },
    {
      header: 'Vulnerabilities',
      accessor: 'vulnerability',
      align: 'left',
      render: renderVulnerabilities,
    },
    entities,
    {
      header: 'CVE',
      accessor: 'cve_ids',
      render: renderPackagesImpacted,
      align: 'left',
    },
    {
      header: 'Last ' + (vulnerabilityType === 'resolved' ? 'Patched' : 'Seen'),
      accessor: 'last_seen',
      render: renderTimeStackedCell,
      align: 'left',
    },
  ];

  switch (resourceType) {
    case Resources.VirtualMachine:
      columns = virtualMachine;
      break;
    case Resources.ServerlessCompute:
      columns = serverless;
      break;
    case NativeResources.ElasticContainerService:
      columns = containers;
      break;
    case CONTAINER_ON_VIRTUAL_MACHINE:
      columns = vmContainerColumns;
      break;
    case NativeResources.ElasticContainerRegistry:
      columns = containerImages;
      break;
    case Resources.Image:
      columns = machineImages;
      break;
    default:
      columns = [];
  }

  let tableColumns = columns;

  const tableActionModals = useMemo(() => {
    const alert: NotificationResource = {
      entity_uuid: currentRow?.uuid ?? currentRow?.id,
      entity_id: currentRow?.resource_id,
      entity_type:
        resourceType === NativeResources.ElasticContainerRegistry &&
        params.tab === VulnerabilityViewByTabs.Resource
          ? NativeResources.ElasticContainerRegistry
          : currentRow?.native_name,
      account_id: currentRow?.account || currentRow?.account_uuid,
      resource:
        resourceType === NativeResources.ElasticContainerRegistry &&
        params.tab === VulnerabilityViewByTabs.Resource
          ? NativeResources.ElasticContainerRegistry
          : currentRow?.native_name,
      entity_name: currentRow?.name || currentRow?.image_tags?.[0],
      environment_id: environmentId!,
    };

    return (
      <>
        {currentRow && ticketModal.isOpen && (
          <CreateTicket
            onClose={ticketModal.onClose}
            isOpen={ticketModal.isOpen}
            alert={alert}
            notification_for={NotificationFor.Vulnerabilities}
          />
        )}
        {currentRow && notificationModal.isOpen && (
          <CreateNotification
            onClose={notificationModal.onClose}
            isOpen={notificationModal.isOpen}
            alert={alert}
            notification_for={NotificationFor.Vulnerabilities}
          />
        )}
        {statusDisclosure.isOpen && (
          <VulnerabilityScanConfirmation
            isOpen={statusDisclosure.isOpen}
            onClose={statusDisclosure.onClose}
            currentResource={currentRow}
            vulnerabilities={currentRow?.vulnerabilities}
            resourceType={resourceType}
          />
        )}
      </>
    );
  }, [
    currentRow,
    resourceType,
    params.tab,
    notificationModal.isOpen,
    notificationModal.onClose,
    ticketModal.isOpen,
    ticketModal.onClose,
    environmentId,
    statusDisclosure.isOpen,
    statusDisclosure.onClose,
  ]);

  return {
    tableColumns,
    tableActionModals,
    handleRowClick,
    cveHostsColumns,
    packagesCVEColumns: cveUnderPackagesColumns,
    hostsDisclosure,
    currentCVE,
    packagesColumns,
    cveColumns,
  };
};
